// Story 7.9 (ADO-1141) — one-click add-to-basket client (FR103); Story 8.2
// (ADO-1093) — the durable line lifecycle (add / update-quantity / remove).
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the basket
// write. The SINGLE consumer seam until the `openapi-typescript`-generated client
// lands (7.1 D-6), exercised against msw in tests. Every mutation returns the FULL
// post-state basket so the store reconciles against server truth in one round-trip.
//
// Story 8.2 landed the real routes on `order-capture`'s `BasketItemsController`:
//   GET    /api/v1/basket/items?depotId=<uuid>                (Story 8.2 ADO-2011)
//   POST   /api/v1/basket/items            { skuCode, depotId, quantity, seenToken, seenRevision? }
//   PATCH  /api/v1/basket/items/:skuCode   { quantity, depotId, seenToken, seenRevision? }
//   DELETE /api/v1/basket/items/:skuCode?depotId=<uuid>&seenToken=<token>[&seenRevision=<n>]
//   DELETE /api/v1/basket/items?depotId=<uuid>&confirm=true   (Story 8.9, ADO-2018)
// The mutations carry the active `depotId` so the stateless server resolves the
// caller's exact DRAFT cart (a user can hold several parked carts under
// park-and-switch). Story 8.3 (ADO-2012), D-1: every call names its cart as a
// `CartTarget`, the depot plus the Trading Account (`customerAccountCode`), the key
// colleagues of one Customer share. Enabler E8-G (ADO-3109), DA-8 (AC9): every cart route also
// takes the entry kind (`entryKind`, a query param on the reads and deletes, a body field on the
// writes, sent only when it is not `DEPOT`), the range part of the cart key, so Chill and
// Fruit & Veg at depot 25 are two carts. The cut-off, order-reference and fulfilment reads are
// keyed by the depot and the account alone (`CartAccount`). The wire shapes are a LOCK-STEP COPY of the DTO the
// order-capture module exports (`BasketStateDto` / `BasketLineDto`) — keep the two
// in sync. Each line carries its durable identity and quantity, its catalogue name and
// image URL (ADO-2263), and the Money locked on it server-side (`unitPrice`, `lineTotal`), so a
// cart total summed from the lines is the money the order will be sent at. ADO-2246: it also
// carries the deposit locked with them (`depositPerUnit` per case, `lineDeposit` for the line),
// never part of either price. A malformed deposit is dropped to absent (unknown) rather than
// rejecting the line, as a display-only field must not break the basket.
// Pricing migration 18 (ADO-2249, DEC-6): a line also carries the price type and the per-case was
// price locked with its unit price (`priceKind`, `wasPrice`), which row 12 (ADO-2243) returns:
// `priceKind` on every line in the quote's lower case, `wasPrice` as money and left out when
// absent (checked against row 12's read by the story's Task 11.1). Both are optional here and
// display-only: an unknown kind or a malformed was price is dropped to absent.

import { isMoneyValue, type MoneyValue } from '../shared/money';
import type { PriceKind } from './price-quotes.client';
import {
  cartAccountQuery,
  cartTargetBody,
  cartTargetQuery,
  type CartAccount,
  type CartTarget,
} from './cart-target';
import { API_PREFIX, apiFetch, PROBLEM_TYPE_HEADER } from './shoplink-api';
// Type-only: `orders.client` imports this module, so a value import would be circular.
import type { FulfilmentRequestFields } from './orders.client';

/** One basket line (lock-step with `BasketLineDto`, Story 8.2). */
export interface BasketLine {
  readonly skuCode: string;
  readonly quantity: number;
  /** The unit price the server locked on the line at add. The
   *  server always sends it; it is optional here because an optimistic line the store
   *  creates before the server answers has none yet. */
  readonly unitPrice?: MoneyValue;
  /** The line's stored total, `unitPrice × quantity`. Optional for
   *  the same reason, and dropped from a line whose quantity the store changes
   *  optimistically, since it no longer matches that quantity. */
  readonly lineTotal?: MoneyValue;
  /** ADO-2246: the deposit per case locked on the line at add (`0.00` when it has none). The
   *  server always sends it; absent here means UNKNOWN (an optimistic line, or a value that did
   *  not parse), which the cart shows as a dash and never as `0.00`. Never part of
   *  `unitPrice` or `lineTotal`. */
  readonly depositPerUnit?: MoneyValue;
  /** ADO-2246: the line's deposit, `depositPerUnit × quantity` in whole cents. Optional for the
   *  same reason, and dropped from a line whose quantity the store changes optimistically,
   *  since it no longer matches that quantity. */
  readonly lineDeposit?: MoneyValue;
  /** Pricing migration 18 (ADO-2249, DEC-6): the line's price type, locked from the same quote
   *  as `unitPrice` (row 12, ADO-2243, sends it on every line; checked by Task 11.1). Absent on
   *  an optimistic line, from a server older than row 12, or when the value is not one of the
   *  four kinds: then the cart shows no price-type pill and no saving for the line. */
  readonly priceKind?: PriceKind;
  /** Pricing migration 18 (ADO-2249, DEC-6): the per-case band price a promotion beat, locked
   *  with `unitPrice`; only a promotion line carries one. Absent means none, never `0.00`; a
   *  malformed one is dropped to absent, so it too counts as none. */
  readonly wasPrice?: MoneyValue;
  /** Story 8.2 (ADO-2011), AC5 — the product's catalogue name (`null` when the SKU
   *  has no catalogue row: the cart shows "Product no longer listed"). The server
   *  always sends it; it is optional here only because an optimistic line the store
   *  creates before the server answers has none yet. */
  readonly name?: string | null;
  /** Story 8.2 (ADO-2011), AC5 — `true` ONLY when the server's catalogue name
   *  lookup failed for this response, so `name: null` does not mean the SKU is
   *  delisted: the cart shows the code without "Product no longer listed" (user
   *  decision, ADO #2011 comment 7282179). Absent otherwise. */
  readonly nameUnavailable?: true;
  /** ADO-2263 — the product's catalogue image URL (already absolute), or `null` when
   *  the catalogue holds none, the SKU has no row, or the lookup failed. The server
   *  always sends it (a malformed value parses as `null`); it is optional here because
   *  an optimistic line the store creates has none yet, and absent renders as `null`. */
  readonly imageUrl?: string | null;
  /** Story 8.10 (ADO-2019), D-7 / AC3: how much of the quantity came from an accepted
   *  HHT order, capped at the new quantity on every quantity change (cases added another
   *  way go first); the part to show is `min(hhtQuantity, quantity)`. Absent on an optimistic
   *  line and on a response from a server that predates it: read it as 0. */
  readonly hhtQuantity?: number;
  /** Story 8.10 (ADO-2019): the SKU this line is the accepted alternative for, or `null`.
   *  Absent on an optimistic line and on an older response: read it as `null`. */
  readonly substitutedFromSku?: string | null;
  /** Story 8.3 (ADO-2012), D-7: the line's token, opaque here, sent back with the next add,
   *  set or remove of this line to say which line it was made from. Absent on an optimistic
   *  line the store creates before the server answers (and on an older server's response). */
  readonly token?: string;
}

/** The caller's active basket (lock-step with `BasketStateDto`, Story 8.2).
 *  An empty basket is `{ lines: [] }`, a valid empty state — never an error.
 *
 *  Story 8.3 (ADO-2012), D-11: it names its cart (`cartId`, `null` with no open cart) and the
 *  cart's `revision`, which only grows within one cart; the store ignores a response for the
 *  cart it shows whose revision is older than the one it shows. An older server sends
 *  neither: read as `null` and 0. */
export interface BasketState {
  readonly cartId: string | null;
  readonly revision: number;
  readonly lines: readonly BasketLine[];
  /** Story 8.3 (ADO-2012), Task 13.5 (card 11); Enabler E8-G (ADO-3109), DA-1: on an add only,
   *  when the add was made from a view of the cart before its latest send, so it went into the
   *  emptied cart (the same cart, AC8): when that send was (ISO 8601 UTC). */
  readonly previousCartSentAt?: string;
  /** Enabler E8-G (ADO-3109), AC16: the reference and the requested delivery date (`YYYY-MM-DD`)
   *  of the cart's latest accepted send, which the cart keeps after its lines go; `null` when
   *  there is none. On the cart read (`listBasket`) only: a write's answer omits them, which
   *  means "unchanged", never "cleared". */
  readonly customerOrderReference?: string | null;
  readonly requestedDeliveryDate?: string | null;
  /** ADO-3257: the cart's latest accepted send, when it went (ISO 8601 UTC) and the cart
   *  revision it was taken from, so the store can tell whether it happened after the cart it
   *  showed (`sentSinceSeen`); `null` when the cart was never sent. On the cart read and card
   *  09's cart only: a write's answer omits it. */
  readonly latestSend?: BasketLatestSend | null;
  /** Story 8.15 (ADO-2024), D-3: the cart's Auto-send opt-in, shared by everyone on the cart;
   *  `null` when it is not armed. On the cart read only: absent means "unknown", never "not
   *  armed". A malformed value is dropped to absent, so the control hides rather than the cart
   *  failing to load. */
  readonly autoSend?: AutoSendState | null;
  /** Story 8.15 (ADO-2024), D-1: how many minutes before the cut-off an armed cart is sent (the
   *  server's `AUTO_SEND_LEAD_MINUTES`), so the control's copy has it before anything is armed.
   *  Absent from a server that does not send it, or when it is not a positive whole number. */
  readonly autoSendLeadMinutes?: number;
}

/** Story 8.15 (ADO-2024): a cart's armed Auto-send (lock-step with `AutoSendStateDto`). */
export interface AutoSendState {
  readonly armed: true;
  /** The minutes before the cut-off the send goes (D-1). */
  readonly leadMinutes: number;
  /** When it was armed, or its choices last saved (ISO 8601 UTC). */
  readonly armedAt: string;
  /** Who armed it, for display only; `null` when unknown. */
  readonly armedByName: string | null;
  /** The reference the send will carry (D-5); `null` for none. */
  readonly customerOrderReference: string | null;
  /** The fulfilment the send will carry (D-5); `null` for none (an arm from the side panel). */
  readonly fulfilmentMethod: 'DELIVERY' | 'COLLECTION' | null;
  /** The rest of the stored choice (D-5), each `null` for none, so Order Review starts from what
   *  the send will carry: a delivery's requested day (`YYYY-MM-DD`), a collection's day, slot and
   *  contact, and the special instructions. */
  readonly deliveryDate: string | null;
  readonly collectionDate: string | null;
  readonly collectionTime: string | null;
  readonly contactName: string | null;
  readonly specialInstructions: string | null;
}

/** ADO-3257: a cart's latest accepted send (lock-step with `BasketLatestSendDto`). */
export interface BasketLatestSend {
  readonly sentAt: string;
  readonly revision: number;
}

interface BasketResponse {
  readonly cartId?: unknown;
  readonly revision?: unknown;
  readonly lines: readonly BasketLine[];
  readonly previousCartSentAt?: unknown;
  readonly customerOrderReference?: unknown;
  readonly requestedDeliveryDate?: unknown;
  readonly latestSend?: unknown;
  readonly autoSend?: unknown;
  readonly autoSendLeadMinutes?: unknown;
}

/** ADO-3257: `value` as a latest send, or `undefined` when it is not one. */
function latestSendOf(value: unknown): BasketLatestSend | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { sentAt, revision } = value as Record<string, unknown>;
  if (typeof sentAt !== 'string' || typeof revision !== 'number' || !Number.isInteger(revision)) {
    return undefined;
  }
  return { sentAt, revision };
}

/** Story 8.15 (ADO-2024): a positive whole number of minutes. */
function isLeadMinutes(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) > 0;
}

/** Story 8.15 (ADO-2024): the stored choice fields of an armed Auto-send (D-5). */
const AUTO_SEND_CHOICE_FIELDS = [
  'deliveryDate',
  'collectionDate',
  'collectionTime',
  'contactName',
  'specialInstructions',
] as const;

/** Story 8.15 (ADO-2024): `value` as an armed Auto-send, or `undefined` when it is not one. A
 *  stored choice field the server does not send reads as none. */
function autoSendStateOf(value: unknown): AutoSendState | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const record = value as Record<string, unknown>;
  const { armed, leadMinutes, armedAt, armedByName, customerOrderReference, fulfilmentMethod } =
    record;
  if (
    armed !== true ||
    !isLeadMinutes(leadMinutes) ||
    typeof armedAt !== 'string' ||
    Number.isNaN(Date.parse(armedAt)) ||
    (armedByName !== null && typeof armedByName !== 'string') ||
    (customerOrderReference !== null && typeof customerOrderReference !== 'string') ||
    (fulfilmentMethod !== null &&
      fulfilmentMethod !== 'DELIVERY' &&
      fulfilmentMethod !== 'COLLECTION')
  ) {
    return undefined;
  }
  const choices = {} as Record<(typeof AUTO_SEND_CHOICE_FIELDS)[number], string | null>;
  for (const field of AUTO_SEND_CHOICE_FIELDS) {
    const choice = record[field] ?? null;
    if (choice !== null && typeof choice !== 'string') return undefined;
    choices[field] = choice;
  }
  return {
    armed,
    leadMinutes,
    armedAt,
    armedByName,
    customerOrderReference,
    fulfilmentMethod,
    ...choices,
  };
}

/** Story 8.15 (ADO-2024): the cart read's Auto-send fields, each dropped when absent or
 *  malformed. */
function autoSendFieldsOf(
  response: BasketResponse | null,
): Pick<BasketState, 'autoSend' | 'autoSendLeadMinutes'> {
  const autoSendValue = response?.autoSend;
  const autoSend =
    autoSendValue === null
      ? null
      : autoSendValue === undefined
        ? undefined
        : autoSendStateOf(autoSendValue);
  const leadMinutes = response?.autoSendLeadMinutes;
  return {
    ...(autoSend === undefined ? {} : { autoSend }),
    ...(isLeadMinutes(leadMinutes) ? { autoSendLeadMinutes: leadMinutes } : {}),
  };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const BASKET_PATH = `${API_PREFIX}/basket/items`;

/**
 * A failed mutation carrying a message the SERVER authored for the end user. Only
 * these are safe to render verbatim beside the card — the store surfaces a
 * `BasketServerError` as-is and replaces every other failure (network error,
 * malformed 2xx, unexpected TypeError) with a generic line, so internal text never
 * leaks into the UI.
 */
export class BasketServerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BasketServerError';
  }
}

/** Story 8.3 (ADO-2012), Task 7.1: the typed problem a refused single-line write answers. */
export const CART_LINE_CONFLICT_PROBLEM = 'cart-line-conflict';

/**
 * Story 8.3 (ADO-2012), Task 10.4 (D-5, D-6): a single add, set or remove refused because the
 * cart line is not the one the Shopper saw (it changed, was removed, or was added since).
 * Nothing was written. It carries the line as it is now (`null`: removed) and the whole cart
 * (any cart action refreshes it, D-13), so the store can show the real state and the UI the
 * conflict message. Its `message` is the server's neutral copy; the UI words its own from
 * the designer's text (D-26).
 */
export class BasketLineConflictError extends Error {
  constructor(
    message: string,
    readonly skuCode: string,
    readonly currentLine: { readonly quantity: number; readonly token: string } | null,
    readonly basket: BasketState,
  ) {
    super(message);
    this.name = 'BasketLineConflictError';
  }
}

/** Story 8.3 (ADO-2012), Task 13.2: the `x-shoplink-problem-type` of a write to a cart a send
 *  holds. */
export const CART_SENDING_PROBLEM = 'cart-sending';

/** Story 8.3 (ADO-2012), Task 13.2: a line write, a clear or an add refused because a send holds
 *  the cart (usually under a second). Nothing was written; the store retries once. */
export class CartSendingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CartSendingError';
  }
}

/** Story 8.3 (ADO-2012), Task 13.4: the `x-shoplink-problem-type` of a change naming a sent cart. */
export const CART_ALREADY_SENT_PROBLEM = 'cart-already-sent';

/** Story 8.3 (ADO-2012), Task 13.4 (card 10); Enabler E8-G (ADO-3109), DA-1: a change or a
 *  removal refused because it was made from a view of the cart before its latest send. Nothing was
 *  written. When that send was, and the cart now (the same cart, emptied by the send and moved on
 *  to a newer revision, AC8). */
export class CartAlreadySentError extends Error {
  constructor(
    message: string,
    readonly sentAt: string,
    readonly basket: BasketState,
  ) {
    super(message);
    this.name = 'CartAlreadySentError';
  }
}

/** Story 8.3 (ADO-2012), Task 6: the `x-shoplink-problem-type` of a refused clear or replace. */
export const CART_CHANGED_SINCE_SEEN_PROBLEM = 'cart-changed-since-seen';

/**
 * Story 8.3 (ADO-2012), Task 6 (cards 06, 07; D-21, D-22): a clear or a replace refused because
 * the cart changed since the Shopper was shown it and still has lines. Nothing was written. It
 * carries the cart's line and case counts now (the dialogs quote them) and the cart itself, so
 * the screen refreshes and a confirm sends the revision just shown.
 */
export class CartChangedSinceSeenError extends Error {
  constructor(
    message: string,
    readonly lineCount: number,
    readonly caseCount: number,
    readonly basket: BasketState,
  ) {
    super(message);
    this.name = 'CartChangedSinceSeenError';
  }
}

/** A wire line is only usable once it carries the durable facts the store keys on
 *  (`skuCode` string + `quantity` number) — a `null`/`{}` line would crash the
 *  store's `quantityMap` with a cryptic TypeError otherwise. */
function isBasketLine(line: unknown): line is BasketLine {
  if (typeof line !== 'object' || line === null) return false;
  const {
    skuCode,
    quantity,
    name,
    nameUnavailable,
    unitPrice,
    lineTotal,
    hhtQuantity,
    substitutedFromSku,
    token,
  } = line as BasketLine;
  return (
    typeof skuCode === 'string' &&
    typeof quantity === 'number' &&
    // Story 8.3 (ADO-2012), D-7: tolerated when absent (an older server), typed when present.
    (token === undefined || typeof token === 'string') &&
    // Story 8.10 (ADO-2019): tolerated when absent (an older server), typed when present.
    (hhtQuantity === undefined || typeof hhtQuantity === 'number') &&
    (substitutedFromSku === undefined ||
      substitutedFromSku === null ||
      typeof substitutedFromSku === 'string') &&
    // `name` is required on the wire (the server always sends a string or null); the
    // optional `undefined` in `BasketLine` exists only for the store's own optimistic
    // lines, which never pass through this parser.
    (name === null || typeof name === 'string') &&
    // `nameUnavailable` is either absent or exactly `true` (lookup failed).
    (nameUnavailable === undefined || nameUnavailable === true) &&
    // The locked money: absent (unknown) or a well-formed Money value.
    (unitPrice === undefined || isMoneyValue(unitPrice)) &&
    (lineTotal === undefined || isMoneyValue(lineTotal))
  );
}

/**
 * Parse a basket response body, VALIDATING that `lines` is an array of well-formed
 * lines. A 2xx whose body isn't the expected shape (empty body, `{}`, `{ lines:
 * null }`, or `{ lines: [null] }` / `{ lines: [{}] }`) is a contract violation:
 * surface it as a clear error rather than letting a downstream `undefined.skuCode`
 * throw a cryptic TypeError that the store would then show as the failure reason.
 */
/** Story 8.3 (ADO-2012), card 09: the cart a refused send returns, read as any basket state. */
export function parseBasketState(body: unknown): BasketState {
  return parseBasket(body);
}

function parseBasket(body: unknown): BasketState {
  const response = body as BasketResponse | null;
  const lines = response?.lines;
  if (!Array.isArray(lines) || !lines.every(isBasketLine)) {
    throw new Error('Basket response was not in the expected shape');
  }
  // Story 8.3 (ADO-2012), D-11: absent on an older server (no cart, revision 0); a present
  // value of the wrong type is contract drift.
  const cartId = response?.cartId ?? null;
  const revision = response?.revision ?? 0;
  if (
    (cartId !== null && typeof cartId !== 'string') ||
    typeof revision !== 'number' ||
    !Number.isInteger(revision)
  ) {
    throw new Error('Basket response was not in the expected shape');
  }
  const sentAt = response?.previousCartSentAt;
  // Enabler E8-G (ADO-3109), AC16: absent from a write's answer; present, a string or null.
  const reference = response?.customerOrderReference;
  const deliveryDate = response?.requestedDeliveryDate;
  if (
    (reference !== undefined && reference !== null && typeof reference !== 'string') ||
    (deliveryDate !== undefined &&
      deliveryDate !== null &&
      (typeof deliveryDate !== 'string' || !ISO_DAY.test(deliveryDate)))
  ) {
    throw new Error('Basket response was not in the expected shape');
  }
  // ADO-3257: absent from a write's answer; present, a send or null.
  const latestSendValue = response?.latestSend;
  const latestSend =
    latestSendValue === undefined || latestSendValue === null
      ? latestSendValue
      : latestSendOf(latestSendValue);
  if (latestSendValue !== undefined && latestSend === undefined) {
    throw new Error('Basket response was not in the expected shape');
  }
  return {
    cartId,
    revision,
    lines: lines.map((line) => withPriceExplanation(withDeposit(withImageUrl(line)))),
    ...(typeof sentAt === 'string' ? { previousCartSentAt: sentAt } : {}),
    ...(reference === undefined ? {} : { customerOrderReference: reference }),
    ...(deliveryDate === undefined ? {} : { requestedDeliveryDate: deliveryDate }),
    ...(latestSend === undefined ? {} : { latestSend }),
    ...autoSendFieldsOf(response),
  };
}

/** Story 8.3 (ADO-2012), Task 10.4: the typed line conflict in a 409 body, or null when the
 *  body is not one (a plain 409, or a typed one that does not carry its members). */
function lineConflictFrom(res: Response, body: unknown): BasketLineConflictError | null {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const typed =
    res.headers.get('x-shoplink-problem-type') === CART_LINE_CONFLICT_PROBLEM ||
    record['type'] === `https://shoplink.bwg.ie/errors/${CART_LINE_CONFLICT_PROBLEM}`;
  if (!typed) return null;
  const { skuCode, currentLine, detail } = record;
  if (typeof skuCode !== 'string') return null;
  const line = currentLine as { quantity?: unknown; token?: unknown } | null | undefined;
  const current =
    line === null
      ? null
      : typeof line === 'object' &&
          line !== undefined &&
          typeof line.quantity === 'number' &&
          typeof line.token === 'string'
        ? { quantity: line.quantity, token: line.token }
        : undefined;
  if (current === undefined) return null;
  let basket: BasketState;
  try {
    basket = parseBasket(record['basket']);
  } catch {
    return null;
  }
  return new BasketLineConflictError(
    typeof detail === 'string' ? detail : 'This line in your cart changed.',
    skuCode,
    current,
    basket,
  );
}

/** Story 8.3 (ADO-2012), Task 6: the typed cart-changed refusal in a 409 body, or null when
 *  the body is not one (a plain 409, or a typed one that does not carry its members). */
function cartChangedFrom(res: Response, body: unknown): CartChangedSinceSeenError | null {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const typed =
    res.headers.get('x-shoplink-problem-type') === CART_CHANGED_SINCE_SEEN_PROBLEM ||
    record['type'] === `https://shoplink.bwg.ie/errors/${CART_CHANGED_SINCE_SEEN_PROBLEM}`;
  if (!typed) return null;
  const { lineCount, caseCount, detail } = record;
  if (!Number.isInteger(lineCount) || !Number.isInteger(caseCount)) return null;
  let basket: BasketState;
  try {
    basket = parseBasket(record['basket']);
  } catch {
    return null;
  }
  return new CartChangedSinceSeenError(
    typeof detail === 'string' ? detail : 'Your cart changed.',
    lineCount as number,
    caseCount as number,
    basket,
  );
}

/** Story 8.3 (ADO-2012), Tasks 13.2 and 13.4: a held cart or a sent cart in a 409 body, or null
 *  when the body is neither. */
function sentOrSendingFrom(res: Response, body: unknown): Error | null {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const typeOf = (problem: string) =>
    res.headers.get('x-shoplink-problem-type') === problem ||
    record['type'] === `https://shoplink.bwg.ie/errors/${problem}`;
  const detail = typeof record['detail'] === 'string' ? record['detail'] : null;
  if (typeOf(CART_SENDING_PROBLEM)) {
    return new CartSendingError(detail ?? 'Your cart is being sent. Try again in a moment.');
  }
  if (!typeOf(CART_ALREADY_SENT_PROBLEM) || typeof record['sentAt'] !== 'string') return null;
  try {
    return new CartAlreadySentError(
      detail ?? 'This cart was already sent.',
      record['sentAt'],
      parseBasket(record['basket']),
    );
  } catch {
    return null;
  }
}

/** ADO-2263 — the image is display-only, so an unusable wire value is not a contract
 *  failure: a present `imageUrl` that is not a string reads as `null`. An absent one stays
 *  absent (the row renders an absent image exactly as `null`: the placeholder). */
function withImageUrl(line: BasketLine): BasketLine {
  const { imageUrl } = line as { imageUrl?: unknown };
  if (imageUrl === undefined || imageUrl === null || typeof imageUrl === 'string') return line;
  return { ...line, imageUrl: null };
}

/** ADO-2246: a deposit that is not well-formed Money is dropped to absent (unknown), not a
 *  contract failure that rejects the line: the cart then shows a dash for it. An absent one
 *  stays absent. */
function withDeposit(line: BasketLine): BasketLine {
  const { depositPerUnit, lineDeposit, ...rest } = line;
  return {
    ...rest,
    ...(isMoneyValue(depositPerUnit) ? { depositPerUnit } : {}),
    ...(isMoneyValue(lineDeposit) ? { lineDeposit } : {}),
  };
}

const PRICE_KINDS: ReadonlySet<unknown> = new Set<PriceKind>([
  'promotion',
  'contract',
  'wow',
  'standard',
]);

/** Pricing migration 18 (ADO-2249, DEC-6): whether a cart line's wire `priceKind` is one of the
 *  four kinds, in the quote's lower case, as row 12 sends them (Task 11.1). */
export function isPriceKind(value: unknown): value is PriceKind {
  return PRICE_KINDS.has(value);
}

/** Pricing migration 18 (ADO-2249, DEC-6): the price type and the was price are display-only, so
 *  an unusable wire value is dropped to absent rather than rejecting the line. An absent one stays
 *  absent. The was price keeps only its two Money fields. */
function withPriceExplanation(line: BasketLine): BasketLine {
  const { priceKind, wasPrice, ...rest } = line as Omit<BasketLine, 'priceKind' | 'wasPrice'> & {
    readonly priceKind?: unknown;
    readonly wasPrice?: unknown;
  };
  return {
    ...rest,
    ...(isPriceKind(priceKind) ? { priceKind } : {}),
    ...(isMoneyValue(wasPrice)
      ? { wasPrice: { amount: wasPrice.amount, currency: wasPrice.currency } }
      : {}),
  };
}

/**
 * Build the Error for a failed mutation, PRESERVING the server's user-facing message
 * ONLY on a 400 (favourites.client precedent). A 400 is the sole status whose body
 * carries a message authored FOR the customer — the AC3 server-side orderability
 * block (`CURRENTLY_UNAVAILABLE_MESSAGE`), the FR33 price-unresolvable message, and
 * Nest's validation errors — so it alone becomes a `BasketServerError` the store
 * renders verbatim.
 *
 * The app's GLOBAL RFC 7807 Problem Details filter renders that text in `detail`
 * (verified against `problem-details.filter.spec.ts`: a `BadRequestException('…')`
 * → `body.detail`); a raw Nest 400 or a test double may instead carry it in
 * `message`. Read `detail` first, then fall back to `message` (string or the
 * class-validator array). Any OTHER status (401/404/5xx/network) is gated out to a
 * status-coded plain Error the store replaces with a generic line, so an internal
 * body (`"Unauthorized"`, a route string) never reaches the card.
 */
async function mutationError(res: Response, fallback: string): Promise<Error> {
  // Story 8.3 (ADO-2012), Task 10.4: a refused single-line write is the typed 409, parsed for
  // its extension members; any other 409 stays a plain status-coded error.
  if (res.status === 409) {
    try {
      const body: unknown = await res.json();
      const conflict =
        lineConflictFrom(res, body) ?? cartChangedFrom(res, body) ?? sentOrSendingFrom(res, body);
      if (conflict !== null) return conflict;
    } catch {
      // Body wasn't JSON — fall through to the generic message.
    }
  }
  if (res.status === 400) {
    try {
      const body: unknown = await res.json();
      const record = body as { detail?: unknown; message?: unknown } | null;
      const detail = record?.detail;
      if (typeof detail === 'string' && detail.length > 0) return new BasketServerError(detail);
      const message = record?.message;
      if (typeof message === 'string' && message.length > 0) return new BasketServerError(message);
      if (Array.isArray(message) && message.length > 0)
        return new BasketServerError(message.join(', '));
    } catch {
      // Body wasn't JSON (or was empty) — fall through to the generic message.
    }
  }
  return new Error(`${fallback} (${res.status})`);
}

/** Fetch the basket of `cart` for hydration (Story 8.2 ADO-2011, AC5: a find-only
 *  read, so no basket is opened by it). Empty is a valid empty state; any non-2xx is
 *  a real read failure the store flags for a retry. */
export async function listBasket(cart: CartTarget): Promise<BasketState> {
  const res = await apiFetch(`${BASKET_PATH}?${cartTargetQuery(cart)}`);
  if (!res.ok) throw new Error(`Failed to load basket (${res.status})`);
  return parseBasket(await res.json());
}

/**
 * Add `quantity` of `skuCode` to `cart` (AC1/AC2). The server creates
 * the line at that quantity when absent, or SUMS it onto the existing line's quantity
 * when present (Story 8.2 ADO-2011) — NEVER a duplicate line. Returns the full
 * post-add basket (carrying the line's new total) so the store reconciles in one
 * round-trip.
 */
export async function addToBasket(
  skuCode: string,
  cart: CartTarget,
  quantity = 1,
  /** Story 8.3 (ADO-2012), D-5/D-7: the token of the line the Shopper saw, `null` for none. */
  seenToken: string | null = null,
  /** Enabler E8-G (ADO-3109), DA-1 (card 11): the revision of the cart on screen; omitted with
   *  no cart on screen. */
  seenRevision?: number,
): Promise<BasketState> {
  const res = await apiFetch(BASKET_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      skuCode,
      ...cartTargetBody(cart),
      quantity,
      seenToken,
      ...(seenRevision === undefined ? {} : { seenRevision }),
    }),
  });
  if (!res.ok) throw await mutationError(res, 'Failed to add to basket');
  return parseBasket(await res.json());
}

/**
 * Set the ABSOLUTE quantity of `skuCode` in `cart` (AC2).
 * The locked unit price is unchanged; `quantity: 0` removes the line. Returns the
 * full post-state basket.
 */
export async function updateBasketLineQuantity(
  skuCode: string,
  quantity: number,
  cart: CartTarget,
  /** Story 8.3 (ADO-2012), D-6/D-7: the token of the line the Shopper changed. */
  seenToken: string,
  /** Enabler E8-G (ADO-3109), DA-1 (card 10): the revision of the cart on screen; omitted with
   *  no cart on screen. */
  seenRevision?: number,
): Promise<BasketState> {
  const res = await apiFetch(`${BASKET_PATH}/${encodeURIComponent(skuCode)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quantity,
      ...cartTargetBody(cart),
      seenToken,
      ...(seenRevision === undefined ? {} : { seenRevision }),
    }),
  });
  if (!res.ok) throw await mutationError(res, 'Failed to update basket');
  return parseBasket(await res.json());
}

/**
 * Remove `skuCode` from `cart` (AC3). Returns the full post-remove basket so the
 * store reconciles the total.
 */
export async function removeBasketLine(
  skuCode: string,
  cart: CartTarget,
  /** Story 8.3 (ADO-2012), D-6/D-7: the token of the line the Shopper removed. */
  seenToken: string,
  /** Enabler E8-G (ADO-3109), DA-1 (card 10): the revision of the cart on screen; omitted with
   *  no cart on screen. */
  seenRevision?: number,
): Promise<BasketState> {
  const revision = seenRevision === undefined ? '' : `&seenRevision=${seenRevision}`;
  const res = await apiFetch(
    `${BASKET_PATH}/${encodeURIComponent(skuCode)}?${cartTargetQuery(cart)}&seenToken=${encodeURIComponent(seenToken)}${revision}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw await mutationError(res, 'Failed to remove from basket');
  return parseBasket(await res.json());
}

// ── Story 8.8 (ADO-2017): the free-delivery status read ─────────────────────
//
//   GET /api/v1/basket/free-delivery-status?depotId=<uuid>[&customerAccountCode=][&entryKind=]
//
// Enabler E8-G (ADO-3109), DA-8: read for the cart, the range included, as SL1.0 reads it from
// that cart's own total (`engine/get_cart.php:1548-1565`; Fruit & Veg has no threshold there).
//
// How much more the store needs to spend at the depot for free delivery, counting what it has
// already sent there (to both Ambient and WOW under their shared threshold, Story 8.E8-F)
// since its last cut-off, or since midnight where the threshold is set to count that way or
// the depot has no cut-off for the store. The server works out the threshold's group; the
// wire is unchanged. A LOCK-STEP COPY of `FreeDeliveryStatusDto` in order-capture; keep the
// two in sync.
// Advisory: the order is never blocked or changed by it (AC2), so a caller hides the
// message on any failure rather than showing an error.

/** Money as the API serialises `@shoplink/domain` Money. */
export interface FreeDeliveryMoney {
  readonly amount: number;
  readonly currency: string;
}

/** `NONE`: the depot has no threshold, say nothing about one (AC3). `BELOW`: the spend
 *  is short of the threshold by `shortfall`. `MET`: free delivery is qualified. */
export type FreeDeliveryStatus =
  | { readonly state: 'NONE' }
  | {
      readonly state: 'BELOW';
      readonly threshold: FreeDeliveryMoney;
      readonly spend: FreeDeliveryMoney;
      readonly shortfall: FreeDeliveryMoney;
    }
  | {
      readonly state: 'MET';
      readonly threshold: FreeDeliveryMoney;
      readonly spend: FreeDeliveryMoney;
    };

export const FREE_DELIVERY_STATUS_PATH = `${API_PREFIX}/basket/free-delivery-status`;

/** Validate a status body. Anything but the three documented shapes is a contract
 *  violation, surfaced as an error so the caller hides the message instead of painting
 *  `undefined`. */
function parseFreeDeliveryStatus(body: unknown): FreeDeliveryStatus {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const { state, threshold, spend, shortfall } = record;
  if (state === 'NONE') return { state };
  if (
    state === 'BELOW' &&
    isMoneyValue(threshold) &&
    isMoneyValue(spend) &&
    isMoneyValue(shortfall)
  ) {
    return { state, threshold, spend, shortfall };
  }
  if (state === 'MET' && isMoneyValue(threshold) && isMoneyValue(spend)) {
    return { state, threshold, spend };
  }
  throw new Error('Free-delivery status response was not in the expected shape');
}

/**
 * Read the free-delivery status for `cart`. `signal` lets the caller abort a request
 * that a depot switch or unmount has made stale. Any non-2xx is an error.
 */
export async function getFreeDeliveryStatus(
  cart: CartTarget,
  options: { readonly signal?: AbortSignal } = {},
): Promise<FreeDeliveryStatus> {
  const res = await apiFetch(
    `${FREE_DELIVERY_STATUS_PATH}?${cartTargetQuery(cart)}`,
    options.signal === undefined ? {} : { signal: options.signal },
  );
  if (!res.ok) throw new Error(`Failed to load free-delivery status (${res.status})`);
  return parseFreeDeliveryStatus(await res.json());
}

/**
 * Story 8.9 (ADO-2018) — clear `cart` for everyone on it. The
 * server checks the confirmation (`confirm=true`, Gate Decision 2) and rejects a
 * request without it; the caller has already confirmed in the dialog. Returns the
 * post-clear basket (`{ lines: [] }`).
 */
export async function clearBasket(cart: CartTarget, seenRevision?: number): Promise<BasketState> {
  // Story 8.3 (ADO-2012), Task 6 (D-22): the revision the Shopper was shown, when known.
  const revision = seenRevision === undefined ? '' : `&seenRevision=${seenRevision}`;
  const res = await apiFetch(`${BASKET_PATH}?${cartTargetQuery(cart)}&confirm=true${revision}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw await mutationError(res, 'Failed to clear the basket');
  return parseBasket(await res.json());
}

// ── Story 8.7 (ADO-2016): working from a previous order ──────────────────────
//
//   POST /api/v1/basket/items/from-order
//        { depotId, customerAccountCode?, entryKind?, orderId, mode: 'ADD' | 'REPLACE', confirm? }
//
// Brings every line of a sent order, at the ordered quantities, into the cart: added to
// it (AC1, AC2) or in place of it (AC3, which the server refuses without
// `confirm: true`). A LOCK-STEP COPY of
// `AddPreviousOrderResultDto` in order-capture; keep the two in sync. Its `basket` is the
// basket state above (lines carry their locked money); the added and not-added lists
// carry none.

export const BASKET_FROM_ORDER_PATH = `${BASKET_PATH}/from-order`;

/** Why a line was not added (AC4). Lock-step with order-capture's
 *  `PREVIOUS_ORDER_NOT_ADDED_REASONS`, pinned by the tenancy suite's lock-step spec. */
export const PREVIOUS_ORDER_NOT_ADDED_REASONS = [
  'PRODUCT_DISCONTINUED',
  'AVAILABILITY_BLOCKED',
  'PRICE_UNRESOLVABLE',
  'QUANTITY_LIMIT',
  // Story 8.3 (ADO-2012), D-23 (card 08): the cart line changed since the Shopper saw it.
  'CHANGED_IN_CART',
] as const;
export type PreviousOrderNotAddedReason = (typeof PREVIOUS_ORDER_NOT_ADDED_REASONS)[number];

export interface PreviousOrderLineQuantity {
  readonly skuCode: string;
  readonly quantity: number;
}

export interface PreviousOrderLineNotAdded {
  readonly skuCode: string;
  /** `null` when the product has no catalogue row or its name could not be read. */
  readonly productName: string | null;
  readonly reason: PreviousOrderNotAddedReason;
  /** Story 8.3 (ADO-2012), D-23: for `CHANGED_IN_CART` only, the cart line's quantity now. */
  readonly currentQuantity?: number;
}

export interface AddFromPreviousOrderResult {
  /** The caller's basket after the write. */
  readonly basket: BasketState;
  /** What this request added, per SKU (not the line's new total). */
  readonly added: readonly PreviousOrderLineQuantity[];
  readonly notAdded: readonly PreviousOrderLineNotAdded[];
}

export type AddFromPreviousOrderRequest =
  /** Story 8.3 (ADO-2012), D-23: an ADD sends the cart lines the Shopper saw (their tokens),
   *  so a line that changed since is left out rather than summed onto. */
  | {
      readonly mode: 'ADD';
      readonly seenLines?: ReadonlyArray<{ readonly skuCode: string; readonly token: string }>;
    }
  /** Story 8.3 (ADO-2012), Task 6 (card 07): a REPLACE sends the revision the Shopper was
   *  shown, so a cart that changed since is not replaced unseen. */
  | { readonly mode: 'REPLACE'; readonly confirm: true; readonly seenRevision?: number };

function isLineQuantity(value: unknown): value is PreviousOrderLineQuantity {
  if (typeof value !== 'object' || value === null) return false;
  const { skuCode, quantity } = value as Partial<PreviousOrderLineQuantity>;
  return typeof skuCode === 'string' && typeof quantity === 'number';
}

function isLineNotAdded(value: unknown): value is PreviousOrderLineNotAdded {
  if (typeof value !== 'object' || value === null) return false;
  const { skuCode, productName, reason, currentQuantity } =
    value as Partial<PreviousOrderLineNotAdded>;
  return (
    typeof skuCode === 'string' &&
    (productName === null || typeof productName === 'string') &&
    (PREVIOUS_ORDER_NOT_ADDED_REASONS as readonly unknown[]).includes(reason) &&
    (currentQuantity === undefined || typeof currentQuantity === 'number')
  );
}

function parseAddFromPreviousOrder(body: unknown): AddFromPreviousOrderResult {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const { added, notAdded } = record;
  const basket = parseBasket(record.basket);
  if (
    !Array.isArray(added) ||
    !added.every(isLineQuantity) ||
    !Array.isArray(notAdded) ||
    !notAdded.every(isLineNotAdded)
  ) {
    throw new Error('Previous-order response was not in the expected shape');
  }
  return { basket, added, notAdded };
}

/** The Problem Details `detail` of a failed response, or null when it has none. */
export async function problemDetail(res: Response): Promise<string | null> {
  try {
    const detail = ((await res.json()) as { detail?: unknown } | null)?.detail;
    return typeof detail === 'string' && detail.length > 0 ? detail : null;
  } catch {
    return null;
  }
}

/**
 * Bring `orderId`'s lines into `cart`. The server decides what is
 * added, so the result lists what was and was not. A 400, 404 or 409 carries a message
 * written for the Shopper (a malformed request, the order is not found, or nothing on the
 * order can be added), surfaced as a `BasketServerError`; anything
 * else is a plain status-coded Error.
 */
export async function addFromPreviousOrder(
  cart: CartTarget,
  orderId: string,
  request: AddFromPreviousOrderRequest,
  options: { readonly signal?: AbortSignal } = {},
): Promise<AddFromPreviousOrderResult> {
  const res = await apiFetch(BASKET_FROM_ORDER_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...cartTargetBody(cart), orderId, ...request }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  if (!res.ok) {
    if (res.status === 409) {
      // Story 8.3 (ADO-2012), Task 6 (card 07): the typed refusal of a replace, read from a
      // copy so a plain 409 still gives its detail below.
      const changed = await res
        .clone()
        .json()
        .then((body: unknown) => cartChangedFrom(res, body) ?? sentOrSendingFrom(res, body))
        .catch(() => null);
      if (changed !== null) throw changed;
    }
    if (res.status === 404 || res.status === 409) {
      const detail = await problemDetail(res);
      if (detail !== null) throw new BasketServerError(detail);
    }
    throw await mutationError(res, 'Failed to add the order to your basket');
  }
  return parseAddFromPreviousOrder(await res.json());
}

// ── Story 8.5 (ADO-2014): the cut-off status read ───────────────────────────
//
//   GET /api/v1/basket/cut-off-status?depotId=<uuid>[&customerAccountCode=<code>]
//
// The next cut-off that applies to the caller at the depot. Story 8.4 (ADO-2013): for the
// Trading Account the cart names (a Value Centre's accounts each have their own); staff name
// none. A LOCK-STEP COPY of
// `CutOffStatusDto` in order-capture; keep the two in sync. Advisory: the server never
// blocks an order on it, so a caller hides the countdown on any failure. Every instant is
// ISO 8601 UTC; `serverNow` lets the client correct its own clock.

/** `NONE`: show nothing (no cut-off at this depot, AC3/AC4). `OPEN`: the next cut-off.
 *  `PASSED`: all of today's cut-offs have passed; `nextDeadline` is the next one. The server
 *  always sets it today (a usable row recurs within a week); `null` is kept on the wire for
 *  forward compatibility (Story 8.6). */
export type CutOffStatus =
  | { readonly state: 'NONE' }
  | { readonly state: 'OPEN'; readonly deadline: string; readonly serverNow: string }
  | {
      readonly state: 'PASSED';
      readonly passedDeadline: string;
      readonly nextDeadline: string | null;
      readonly serverNow: string;
    };

export const CUT_OFF_STATUS_PATH = `${API_PREFIX}/basket/cut-off-status`;

function isInstant(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value));
}

/** Validate a status body; anything but the three documented shapes is contract drift,
 *  surfaced as an error so the caller hides the countdown instead of painting `NaN`. */
function parseCutOffStatus(body: unknown): CutOffStatus {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const { state, deadline, passedDeadline, nextDeadline, serverNow } = record;
  if (state === 'NONE') return { state };
  if (state === 'OPEN' && isInstant(deadline) && isInstant(serverNow)) {
    return { state, deadline, serverNow };
  }
  if (
    state === 'PASSED' &&
    isInstant(passedDeadline) &&
    (nextDeadline === null || isInstant(nextDeadline)) &&
    isInstant(serverNow)
  ) {
    return { state, passedDeadline, nextDeadline, serverNow };
  }
  throw new Error('Cut-off status response was not in the expected shape');
}

/**
 * Read the cut-off status for `cart` (its depot, for its account). `signal` lets the caller
 * abort a request that a switch or unmount has made stale. Any non-2xx is an error.
 */
export async function getCutOffStatus(
  cart: CartAccount,
  options: { readonly signal?: AbortSignal } = {},
): Promise<CutOffStatus> {
  const res = await apiFetch(
    `${CUT_OFF_STATUS_PATH}?${cartAccountQuery(cart)}`,
    options.signal === undefined ? {} : { signal: options.signal },
  );
  if (!res.ok) throw new Error(`Failed to load cut-off status (${res.status})`);
  return parseCutOffStatus(await res.json());
}

// ---------------------------------------------------------------------------------------
// Story 8.14 (ADO-2023), D-6 — the order-reference rule read.
//
//   GET /api/v1/basket/order-reference-rule?depotId=<uuid>[&customerAccountCode=<code>]
//
// Whether the caller's Trading Account at the depot (Story 8.4, ADO-2013: the one the cart
// names) requires a customer order reference, and
// the longest one the send accepts. A LOCK-STEP COPY of `OrderReferenceRuleDto` in
// order-capture; keep the two in sync. Advisory: the send enforces the rule itself, so a
// caller that cannot read it labels the field without a qualifier.

export interface OrderReferenceRule {
  readonly required: boolean;
  readonly maxLength: number;
}

export const ORDER_REFERENCE_RULE_PATH = `${API_PREFIX}/basket/order-reference-rule`;

/** Validate a rule body; anything else is contract drift, surfaced as an error. */
function parseOrderReferenceRule(body: unknown): OrderReferenceRule {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const { required, maxLength } = record;
  if (typeof required === 'boolean' && Number.isInteger(maxLength) && (maxLength as number) > 0) {
    return { required, maxLength: maxLength as number };
  }
  throw new Error('Order reference rule response was not in the expected shape');
}

/**
 * Read the order-reference rule for `cart` (its depot, for its account). `signal` lets the
 * caller abort a request that a switch or unmount has made stale. Any non-2xx is an error.
 */
export async function getOrderReferenceRule(
  cart: CartAccount,
  options: { readonly signal?: AbortSignal } = {},
): Promise<OrderReferenceRule> {
  const res = await apiFetch(
    `${ORDER_REFERENCE_RULE_PATH}?${cartAccountQuery(cart)}`,
    options.signal === undefined ? {} : { signal: options.signal },
  );
  if (!res.ok) throw new Error(`Failed to load the order reference rule (${res.status})`);
  return parseOrderReferenceRule(await res.json());
}

// ---------------------------------------------------------------------------------------
// Story 8.13 (ADO-2022), D-8 — the fulfilment options read.
//
//   GET /api/v1/basket/fulfilment-options?depotId=<uuid>[&customerAccountCode=<code>]
//
// Whether the cart offers a delivery date, collection and special instructions, and what the
// Fulfilment card may offer. A LOCK-STEP COPY of `FulfilmentOptionsDto` in order-capture; keep
// the two in sync. Advisory: the send judges the choice again against what is on offer then.

export interface FulfilmentOptions {
  /** Whether the cart offers the options at all (a Value Centre cart, not LSN). */
  readonly offered: boolean;
  /** The delivery dates on offer (`YYYY-MM-DD`, ascending); the first is the next delivery.
   *  Empty when not offered, or when the store has no schedule at the depot (no date field). */
  readonly deliveryDates: readonly string[];
  /** Today on the branch's wall clock (`YYYY-MM-DD`): the earliest collection date. */
  readonly today: string;
  /** Every collection slot (`HH:MM`, ascending). */
  readonly collectionTimeSlots: readonly string[];
  /** The first slot still open today, or null when none is. */
  readonly firstCollectionSlotToday: string | null;
  readonly contactNameMaxLength: number;
  readonly specialInstructionsMaxLength: number;
}

export const FULFILMENT_OPTIONS_PATH = `${API_PREFIX}/basket/fulfilment-options`;

const OPTIONS_ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const OPTIONS_SLOT = /^\d{2}:\d{2}$/;

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) > 0;
}

/** Validate an options body; anything else is contract drift, surfaced as an error. */
function parseFulfilmentOptions(body: unknown): FulfilmentOptions {
  const record = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>;
  const {
    offered,
    deliveryDates,
    today,
    collectionTimeSlots,
    firstCollectionSlotToday,
    contactNameMaxLength,
    specialInstructionsMaxLength,
  } = record;
  const isDayList = (value: unknown): value is string[] =>
    Array.isArray(value) &&
    value.every((day) => typeof day === 'string' && OPTIONS_ISO_DAY.test(day));
  const isSlotList = (value: unknown): value is string[] =>
    Array.isArray(value) &&
    value.every((slot) => typeof slot === 'string' && OPTIONS_SLOT.test(slot));
  if (
    typeof offered === 'boolean' &&
    isDayList(deliveryDates) &&
    typeof today === 'string' &&
    OPTIONS_ISO_DAY.test(today) &&
    isSlotList(collectionTimeSlots) &&
    (firstCollectionSlotToday === null ||
      (typeof firstCollectionSlotToday === 'string' &&
        OPTIONS_SLOT.test(firstCollectionSlotToday))) &&
    isPositiveInteger(contactNameMaxLength) &&
    isPositiveInteger(specialInstructionsMaxLength)
  ) {
    return {
      offered,
      deliveryDates,
      today,
      collectionTimeSlots,
      firstCollectionSlotToday,
      contactNameMaxLength,
      specialInstructionsMaxLength,
    };
  }
  throw new Error('Fulfilment options response was not in the expected shape');
}

/**
 * Read the fulfilment options for `cart` (its depot, for its account). `signal` lets the caller
 * abort a request that a switch or unmount has made stale. Any non-2xx is an error.
 */
export async function getFulfilmentOptions(
  cart: CartAccount,
  options: { readonly signal?: AbortSignal } = {},
): Promise<FulfilmentOptions> {
  const res = await apiFetch(
    `${FULFILMENT_OPTIONS_PATH}?${cartAccountQuery(cart)}`,
    options.signal === undefined ? {} : { signal: options.signal },
  );
  if (!res.ok) throw new Error(`Failed to load the fulfilment options (${res.status})`);
  return parseFulfilmentOptions(await res.json());
}

// ---------------------------------------------------------------------------------------
// Story 8.15 (ADO-2024), D-3 and D-5: arming and disarming a cart's Auto-send.
//
//   PUT    /api/v1/basket/auto-send   { depotId, customerAccountCode?, entryKind?, cartId,
//                                       customerOrderReference?, fulfilmentMethod?, deliveryDate?,
//                                       collectionDate?, collectionTime?, contactName?,
//                                       specialInstructions? }        -> 200 AutoSendStateDto
//   DELETE /api/v1/basket/auto-send?depotId=[&customerAccountCode=][&entryKind=]   -> 204
//
// A PUT on an armed cart re-saves its choices (the review's reference and fulfilment, D-5). The
// server validates them as a send would, and refuses with the send's own typed problems: a
// reference (`customer-order-reference` 400), a fulfilment choice (`order-fulfilment` 400, with
// `invalidFields`), a depot with no cut-off (`auto-send-unavailable` 409), a cart that is not the
// current one (`cart-changed` 409). The response is a LOCK-STEP COPY of order-capture's
// `AutoSendStateDto`; keep the two in sync.

export const AUTO_SEND_PATH = `${API_PREFIX}/basket/auto-send`;

/** Story 8.15 (ADO-2024): what an arm (or a re-save) carries for the send: the reference and
 *  the fulfilment fields a send takes, each fulfilment field absent for none. A reference that is
 *  present is the one stored (`''` or blank: none); an absent one leaves the server the reference
 *  the cart kept (a side-panel tick with none entered). */
export interface AutoSendChoices extends FulfilmentRequestFields {
  readonly customerOrderReference?: string;
}

/** Story 8.15 (ADO-2024): an arm or a disarm the server refused (a 4xx), with its Problem
 *  Details `detail` and its `x-shoplink-problem-type` slug when it carries one. Nothing was
 *  written. */
export class AutoSendRefusedError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problemType: string | undefined,
    /** With the `order-fulfilment` problem, every refused field with its message. */
    readonly invalidFields: readonly { readonly field: string; readonly message: string }[] = [],
  ) {
    super(message);
    this.name = 'AutoSendRefusedError';
  }
}

const AUTO_SEND_REFUSED_FALLBACK = 'Auto-send could not be changed.';

/** The refusal in a 4xx answer to an Auto-send write, or a plain status-coded Error otherwise. */
async function autoSendFailure(res: Response): Promise<Error> {
  if (res.status < 400 || res.status >= 500) {
    return new Error(`Failed to change Auto-send (${res.status})`);
  }
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = await res.json();
    if (typeof parsed === 'object' && parsed !== null) body = parsed as Record<string, unknown>;
  } catch {
    // Not JSON: the refusal carries no detail.
  }
  const detail =
    typeof body['detail'] === 'string' && body['detail'] !== '' ? body['detail'] : null;
  const invalidFields = Array.isArray(body['invalidFields'])
    ? (body['invalidFields'] as unknown[]).flatMap((entry) => {
        const candidate = entry as { field?: unknown; message?: unknown } | null;
        return typeof candidate?.field === 'string' && typeof candidate.message === 'string'
          ? [{ field: candidate.field, message: candidate.message }]
          : [];
      })
    : [];
  return new AutoSendRefusedError(
    detail ?? AUTO_SEND_REFUSED_FALLBACK,
    res.status,
    res.headers.get(PROBLEM_TYPE_HEADER) ?? undefined,
    invalidFields,
  );
}

/**
 * Arm `cart`'s Auto-send, or re-save its choices when it is armed (D-3, D-5). `cartId` is the
 * cart on screen: the server refuses one that is not the current cart. Resolves the state as
 * the server stored it.
 */
export async function armAutoSend(
  cart: CartTarget,
  cartId: string,
  choices: AutoSendChoices,
): Promise<AutoSendState> {
  const res = await apiFetch(AUTO_SEND_PATH, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...cartTargetBody(cart), cartId, ...choices }),
  });
  if (!res.ok) throw await autoSendFailure(res);
  const state = autoSendStateOf(await res.json());
  if (state === undefined) throw new Error('Auto-send response was not in the expected shape');
  return state;
}

/** Disarm `cart`'s Auto-send (D-7). Succeeds when it was not armed. */
export async function disarmAutoSend(cart: CartTarget): Promise<void> {
  const res = await apiFetch(`${AUTO_SEND_PATH}?${cartTargetQuery(cart)}`, { method: 'DELETE' });
  if (!res.ok) throw await autoSendFailure(res);
}
